import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LocationStrategy } from '@angular/common';
import { BaseService } from "./base.service";
import {
  IApiResponse,
  IAprovarMatrizPreliminarDto,
  IAtivarConstatacaoDto,
  IAvaliarManifestacaoDto,
  ICancelarConstatacaoDto,
  IConstatacaoModel,
  ICriarMatrizConstatacaoModel,
  IEnviarManifestacaoDto,
  IFinalizarConstatacaoDto,
  IGerarMatrizConsolidadaDto,
  IGerarMatrizPreliminarDto,
  IManifestacaoAuditadoModel,
  IMatrizConstatacaoModel,
  IMatrizDefinitivaConsolidadaResponse,
  IOsaExecucaoMatrizConstatacaoItemModel,
  IRecomendacaoModel,
  ISalvarConstatacaoDto,
  ISalvarManifestacaoDto,
  IValidacaoMatrizesDefinitivasResponse,
  IValidacaoProgramaTrabalhoResponse
} from "../models/constatacao.model";
import { IArquivoItemModel } from "../models/item-solicitacao-tecnica.model";
import { IBaseModel } from "../models/base.model";



@Injectable({
  providedIn: 'root'
})
export class MatrizConstatacoesService extends BaseService {
  constructor(httpClient: HttpClient, locationStrategy: LocationStrategy) {
    super(httpClient, locationStrategy);
  }

  // ==================== MATRIZ PRELIMINAR ====================

  /**
   * Valida se o Programa de Trabalho está finalizado (Critério 5)
   */
  validarProgramaTrabalho(
    osaExecucaoId: number
  ): Promise<IApiResponse<IValidacaoProgramaTrabalhoResponse>> {
    return this.httpClient
      .get<IApiResponse<IValidacaoProgramaTrabalhoResponse>>(
        `${this.apiBaseUrl}/matriz-constatacoes/validar-programa-trabalho/${osaExecucaoId}`
      )
      .toPromise();
  }

  obterMatrizDefinitivaConsolidada(osaExecucaoId: number): Promise<IApiResponse<IMatrizDefinitivaConsolidadaResponse>> {
    return this.httpClient
      .get<IApiResponse<IMatrizDefinitivaConsolidadaResponse>>(
        `${this.apiBaseUrl}/matriz-constatacoes/definitiva-consolidada/${osaExecucaoId}`
      )
      .toPromise();
  }

  public criarMatrizConstatacao(
    osaExecucaoId: number
  ): Promise<IApiResponse<IMatrizConstatacaoModel>> {

    const model: ICriarMatrizConstatacaoModel = {
      id: 0,
      osaExecucaoId
    };

    return this.httpClient
      .post<IApiResponse<IMatrizConstatacaoModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes`,
        model
      )
      .toPromise();
  }
  salvarItemConstatacao(
    id: number,
    dto: any
  ): Promise<IApiResponse<IOsaExecucaoMatrizConstatacaoItemModel>> {
    return this.httpClient
      .put<IApiResponse<IOsaExecucaoMatrizConstatacaoItemModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/item/${id}/salvar`,
        dto
      )
      .toPromise();
  }

  /**
   * Finalizar item da constatação - novo endpoint
   * POST /matriz-constatacoes/item/{id}/finalizar
   */
  finalizarItemConstatacao(
    id: number
  ): Promise<IApiResponse<IOsaExecucaoMatrizConstatacaoItemModel>> {
    return this.httpClient
      .post<IApiResponse<IOsaExecucaoMatrizConstatacaoItemModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/item/${id}/finalizar`,
        { id }
      )
      .toPromise();
  }

  /**
   * Recomendações disponíveis da matriz (para o select) - novo endpoint
   * GET /matriz-constatacoes/{matrizId}/recomendacoes
   */
  obterRecomendacoesMatriz(matrizId: number): Promise<IApiResponse<IRecomendacaoModel[]>> {
    return this.httpClient
      .get<IApiResponse<IRecomendacaoModel[]>>(
        `${this.apiBaseUrl}/matriz-constatacoes/${matrizId}/recomendacoes`
      )
      .toPromise();
  }

  obterRecomendacoes(): Promise<IApiResponse<IRecomendacaoModel[]>> {
    return this.httpClient
      .get<IApiResponse<IRecomendacaoModel[]>>(
        `${this.apiBaseUrl}/matriz-constatacoes/recomendacoes`
      )
      .toPromise();
  }

  obterRecomendacoesMatrizDefinitivaPorOsaExecucao(osaExecucaoId: number): Promise<IApiResponse<IRecomendacaoModel[]>> {
    return this.httpClient
      .get<IApiResponse<IRecomendacaoModel[]>>(
        `${this.apiBaseUrl}/matriz-constatacoes/definitiva/recomendacoes/osaExecucao/${osaExecucaoId}`
      )
      .toPromise();
  }

  public habilitarProgramaTrabalho(osaExecucaoId: number): Promise<IBaseModel<any>> {
    return this.httpClient
      .post<IBaseModel<any>>(`${this.apiBaseUrl}/OsaExecucaoProgramaTrabalho/programa-trabalho/habilitar/osaExecucaoid/${osaExecucaoId}`, {})
      .toPromise();
  }

  /**
   * Gera a Matriz de Constatações Preliminar (Critério 8)
   */
  public gerarMatrizPreliminar(
    osaExecucaoId: number,
    forcarRegeneracao: boolean = false
  ): Promise<IApiResponse<IMatrizConstatacaoModel>> {

    const model = { id: 0, osaExecucaoId, forcarRegeneracao };

    return this.httpClient
      .post<IApiResponse<IMatrizConstatacaoModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes`,
        model
      )
      .toPromise();
  }

  /**
   * Obtém a Matriz de Constatações Preliminar
   */
  obterMatrizPreliminar(osaExecucaoId: number): Promise<IApiResponse<IMatrizConstatacaoModel>> {
    return this.httpClient
      .get<IApiResponse<IMatrizConstatacaoModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/obter-osa-execucao/${osaExecucaoId}`
      )
      .toPromise();
  }
  obterPorId(id: number, excluirCancelados: boolean = false): Promise<IApiResponse<IMatrizConstatacaoModel>> {
    return this.httpClient
      .get<IApiResponse<IMatrizConstatacaoModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/${id}`,
        { params: { excluirCancelados: excluirCancelados.toString() } }
      )
      .toPromise();
  }

  /**
   * Aprova a Matriz de Constatações Preliminar (Critério 17-19)
   */
  aprovarMatrizPreliminar(
    dto: IAprovarMatrizPreliminarDto
  ): Promise<IApiResponse<IMatrizConstatacaoModel>> {
    return this.httpClient
      .put<IApiResponse<IMatrizConstatacaoModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/avaliar`,
        dto
      )
      .toPromise();
  }

  // ==================== CONSTATAÇÕES ====================

  /**
   * Obtém uma constatação específica
   */
  obterConstatacao(id: number): Promise<IApiResponse<IOsaExecucaoMatrizConstatacaoItemModel>> {
    return this.httpClient
      .get<IApiResponse<IOsaExecucaoMatrizConstatacaoItemModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/item/${id}`
      )
      .toPromise();
  }

  /**
   * Salva uma constatação (Critério 12)
   */
  salvarConstatacao(dto: IOsaExecucaoMatrizConstatacaoItemModel): Promise<IApiResponse<IOsaExecucaoMatrizConstatacaoItemModel>> {
    if (dto.id) {
      return this.httpClient
        .put<IApiResponse<IOsaExecucaoMatrizConstatacaoItemModel>>(`${this.apiBaseUrl}/matriz-constatacoes/constatacao/${dto.id}`, dto)
        .toPromise();
    } else {
      return this.httpClient
        .post<IApiResponse<IOsaExecucaoMatrizConstatacaoItemModel>>(`${this.apiBaseUrl}/matriz-constatacoes/constatacao`, dto)
        .toPromise();
    }
  }

  /**
   * Finaliza uma constatação (Critério 13)
   */
  finalizarConstatacao(dto: IFinalizarConstatacaoDto): Promise<IApiResponse<IConstatacaoModel>> {
    return this.httpClient
      .post<IApiResponse<IConstatacaoModel>>(`${this.apiBaseUrl}/matriz-constatacoes/constatacao/finalizar`, dto)
      .toPromise();
  }

  /**
   * Cancela uma constatação da Matriz Definitiva (Critério 31)
   */
  cancelarConstatacao(dto: ICancelarConstatacaoDto): Promise<IApiResponse<IConstatacaoModel>> {
    return this.httpClient
      .post<IApiResponse<IConstatacaoModel>>(`${this.apiBaseUrl}/matriz-constatacoes/constatacao/cancelar`, dto)
      .toPromise();
  }

  /**
   * Ativa uma constatação cancelada (Critério 32)
   */
  ativarConstatacao(constatacaoId: number, usuarioId: string): Promise<IApiResponse<IConstatacaoModel>> {
    const dto: IAtivarConstatacaoDto = {
      constatacaoId,
      usuarioId
    };
    return this.httpClient
      .post<IApiResponse<IConstatacaoModel>>(`${this.apiBaseUrl}/matriz-constatacoes/constatacao/ativar`, dto)
      .toPromise();
  }

  /**
   * Obtém as recomendações já cadastradas na matriz (Critério 14)
   */
  // obterRecomendacoesMatriz(matrizId: number): Promise<IApiResponse<any[]>> {
  //   return this.httpClient
  //     .get<IApiResponse<any[]>>(`${this.apiBaseUrl}/matriz-constatacoes/recomendacoes/${matrizId}`)
  //     .toPromise();
  // }

  // ==================== MANIFESTAÇÃO DO AUDITADO ====================

  /**
   * Obtém a Manifestação do Auditado
   */
  obterManifestacaoAuditado(osaExecucaoId: number): Promise<IApiResponse<IManifestacaoAuditadoModel>> {
    return this.httpClient
      .get<IApiResponse<IManifestacaoAuditadoModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/manifestacao/${osaExecucaoId}`
      )
      .toPromise();
  }

  /**
   * Salva a Manifestação do Auditado (Critério 21)
   */
  public salvarManifestacaoAuditado(
    dto: ISalvarManifestacaoDto
  ): Promise<IApiResponse<IManifestacaoAuditadoModel>> {
    return this.httpClient
      .post<IApiResponse<IManifestacaoAuditadoModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/manifestacao/salvar`,
        dto
      )
      .toPromise();
  }

  /**
   * Envia a Manifestação do Auditado
   */
  enviarManifestacaoAuditado(dto: IEnviarManifestacaoDto): Promise<IApiResponse<IManifestacaoAuditadoModel>> {
    return this.httpClient
      .post<IApiResponse<IManifestacaoAuditadoModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/manifestacao/enviar`,
        dto
      )
      .toPromise();
  }

  /**
   * Avalia a Manifestação do Auditado (Critério 26-27)
   */
  avaliarManifestacaoAuditado(dto: IAvaliarManifestacaoDto): Promise<IApiResponse<IManifestacaoAuditadoModel>> {
    return this.httpClient
      .put<IApiResponse<IManifestacaoAuditadoModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/manifestacao-auditado/avaliar`,
        dto
      )
      .toPromise();
  }

  /**
   * Upload de arquivo da manifestação (Critério 21, 22, 23)
   */
  uploadArquivoManifestacao(
    manifestacaoId: number,
    arquivo: File,
    tipo: 'enviado' | 'recebido'
  ): Promise<IApiResponse<any>> {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    formData.append('tipo', tipo);
    formData.append('manifestacaoId', manifestacaoId.toString());

    return this.httpClient
      .post<IApiResponse<any>>(`${this.apiBaseUrl}/matriz-constatacoes/manifestacao/upload`, formData)
      .toPromise();
  }

  /**
   * Exclusão de arquivo da manifestação
   */
  excluirArquivoManifestacao(arquivoId: number): Promise<IApiResponse<void>> {
    return this.httpClient
      .delete<IApiResponse<void>>(`${this.apiBaseUrl}/matriz-constatacoes/manifestacao/arquivo/${arquivoId}`)
      .toPromise();
  }

  // ==================== MATRIZ DEFINITIVA ====================

  /**
   * Obtém a Matriz de Constatações Definitiva
   */
  obterMatrizDefinitiva(osaExecucaoId: number): Promise<IApiResponse<IMatrizConstatacaoModel>> {
    return this.httpClient
      .get<IApiResponse<IMatrizConstatacaoModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/definitiva/${osaExecucaoId}`
      )
      .toPromise();
  }

  // ==================== MATRIZ CONSOLIDADA ====================

  /**
   * Valida se as Matrizes Definitivas estão finalizadas (Critério 38)
   */
  validarMatrizesDefinitivas(
    osaExecucaoId: number
  ): Promise<IApiResponse<IValidacaoMatrizesDefinitivasResponse>> {
    return this.httpClient
      .get<IApiResponse<IValidacaoMatrizesDefinitivasResponse>>(
        `${this.apiBaseUrl}/matriz-constatacoes/validar-matrizes-definitivas/${osaExecucaoId}`
      )
      .toPromise();
  }

  /**
   * Gera a Matriz de Constatações Consolidada (Critério 38)
   */
  gerarMatrizConsolidada(osaExecucaoId: number): Promise<IApiResponse<IMatrizConstatacaoModel>> {
    return this.httpClient
      .post<IApiResponse<IMatrizConstatacaoModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/definitiva-consolidada/gerar/${osaExecucaoId}`,
        null
      )
      .toPromise();
  }

  /**
   * Obtém a Matriz de Constatações Consolidada
   */
  obterMatrizConsolidada(osaExecucaoId: number): Promise<IApiResponse<IMatrizConstatacaoModel>> {
    return this.httpClient
      .get<IApiResponse<IMatrizConstatacaoModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/consolidada/${osaExecucaoId}`
      )
      .toPromise();
  }

  /**
   * Exclui a Matriz Consolidada (Critério 41)
   */
  public excluirMatrizConsolidada(matrizConsolidadaId: number): Promise<IApiResponse<boolean>> {
    return this.httpClient
      .delete<IApiResponse<boolean>>(
        `${this.apiBaseUrl}/matriz-constatacoes/consolidada/${matrizConsolidadaId}/excluir`
      )
      .toPromise();
  }

  // ==================== HISTÓRICO ====================

  /**
   * Obtém o histórico de uma entidade (Critério 46-49)
   */
  obterHistorico(
    entidadeId: number,
    entidadeTipo: 'matriz' | 'constatacao' | 'manifestacao'
  ): Promise<IApiResponse<any[]>> {
    return this.httpClient
      .get<IApiResponse<any[]>>(
        `${this.apiBaseUrl}/matriz-constatacoes/historico/${entidadeTipo}/${entidadeId}`
      )
      .toPromise();
  }

  // ==================== VISUALIZAÇÃO/IMPRESSÃO ====================

  /**
   * Gera PDF para visualização/impressão da matriz (Critério 43-44)
   */
  public excluirAnexoEnviadoManifestacao(
    manifestacaoId: number,
    arquivo: IArquivoItemModel
  ): Promise<IApiResponse<boolean>> {

    return this.httpClient
      .delete<IApiResponse<boolean>>(
        `${this.apiBaseUrl}/matriz-constatacoes/manifestacao/${manifestacaoId}/anexo-enviado/${arquivo.id}`
      )
      .toPromise();
  }

  public excluirAnexoRecebido(
    manifestacaoAuditadoId: number,
    anexoId: number
  ): Promise<IBaseModel<any>> {
    return this.httpClient
      .delete<IBaseModel<any>>(
        `${this.apiBaseUrl}/matriz-constatacoes/manifestacao/${manifestacaoAuditadoId}/anexo-recebido/${anexoId}/excluir`,
        {}
      )
      .toPromise();
  }

  public excluirAnexoEviado(
    manifestacaoAuditadoId: number,
    anexoId: number
  ): Promise<IBaseModel<any>> {
    return this.httpClient
      .delete<IBaseModel<any>>(
        `${this.apiBaseUrl}/matriz-constatacoes/manifestacao/${manifestacaoAuditadoId}/anexo-enviado/${anexoId}/excluir`,
        {}
      )
      .toPromise();
  }

  public async downloadAnexoEnviadoManifestacaoAuditado(
    manifestacaoAuditadoId: number,
    anexoId: number
  ): Promise<IBaseModel<IArquivoItemModel>> {
    return this.httpClient
      .get<IBaseModel<IArquivoItemModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/manifestacao-auditado/${manifestacaoAuditadoId}/anexo-enviado/${anexoId}/download`,
        {}
      )
      .toPromise();
  }

  public async downloadAnexoRecebidoManifestacaoAuditado(
    manifestacaoAuditadoId: number,
    anexoId: number
  ): Promise<IBaseModel<IArquivoItemModel>> {
    return this.httpClient
      .get<IBaseModel<IArquivoItemModel>>(
        `${this.apiBaseUrl}/matriz-constatacoes/manifestacao-auditado/${manifestacaoAuditadoId}/anexo-recebido/${anexoId}/download`,
        {}
      )
      .toPromise();
  }


}
